<template>
  <div class="container-fluid pm-page">
    <div class="pm-page-header pb-3 d-flex flex-wrap align-items-start justify-content-between gap-3">
      <div>
        <h5 class="mb-0 font-weight-bold">{{ pageTitle }}</h5>
        <p class="text-secondary text-sm mb-0">{{ pageSubtitle }}</p>
      </div>

      <div class="view-toggle" role="tablist" aria-label="View mode">
        <button
          v-for="opt in viewOptions"
          :key="opt.key"
          type="button"
          class="view-toggle__btn"
          :class="{ 'view-toggle__btn--active': viewMode === opt.key }"
          role="tab"
          :aria-selected="viewMode === opt.key ? 'true' : 'false'"
          @click="viewMode = opt.key"
        >
          <i :class="opt.icon" class="me-1" aria-hidden="true"></i>
          {{ opt.label }}
        </button>
      </div>
    </div>

    <div class="row pb-2 pm-page-main">
      <div class="col-12 pm-page-col">
        <div class="card pm-page-card">
          <div v-if="viewMode === 'list'" class="card-header pb-0">
            <div class="tab-shell" role="tablist" aria-label="List scope">
              <button
                type="button"
                class="tab-item"
                :class="{ active: listTab === 'current' }"
                role="tab"
                :aria-selected="listTab === 'current' ? 'true' : 'false'"
                @click="listTab = 'current'"
              >
                Current
              </button>
              <button
                type="button"
                class="tab-item"
                :class="{ active: listTab === 'history' }"
                role="tab"
                :aria-selected="listTab === 'history' ? 'true' : 'false'"
                @click="listTab = 'history'"
              >
                History
              </button>
            </div>
          </div>

          <div class="card-body px-0 pt-0 pb-2 pm-page-tab-body">
            <div class="po-mgmt-inset">
              <div v-if="loading" class="text-secondary text-sm py-5 text-center flex-grow-1 d-flex align-items-center justify-content-center">
                <span><i class="fas fa-spinner fa-spin me-2"></i>Loading sub-projects...</span>
              </div>

              <div v-else-if="error" class="alert alert-danger text-sm py-2 mb-0" role="alert">
                {{ error }}
              </div>

              <!-- List -->
              <template v-else-if="viewMode === 'list'">
                <div class="po-list-toolbar">
                  <div class="po-list-filter">
                    <label class="form-label text-xs text-secondary mb-1">Ordering</label>
                    <base-select
                      size="sm"
                      :model-value="listOrdering"
                      :options="orderingOptions"
                      option-label="label"
                      option-value="value"
                      placeholder="Ordering"
                      @update:model-value="listOrdering = $event"
                    />
                  </div>
                  <div class="po-list-filter">
                    <label class="form-label text-xs text-secondary mb-1">Team</label>
                    <base-select
                      size="sm"
                      :model-value="listTeamFilter"
                      :options="teamFilterOptions"
                      option-label="label"
                      option-value="value"
                      placeholder="All Teams"
                      @update:model-value="listTeamFilter = $event"
                    />
                  </div>
                  <div class="po-list-filter">
                    <label class="form-label text-xs text-secondary mb-1">PM / PO</label>
                    <base-select
                      size="sm"
                      :model-value="listPmFilter"
                      :options="pmFilterOptions"
                      option-label="label"
                      option-value="value"
                      placeholder="All PM / PO"
                      @update:model-value="listPmFilter = $event"
                    />
                  </div>
                  <div class="po-list-filter">
                    <label class="form-label text-xs text-secondary mb-1">Status</label>
                    <base-select
                      size="sm"
                      :model-value="listStatusFilter"
                      :options="statusFilterOptions"
                      option-label="label"
                      option-value="value"
                      placeholder="All Status"
                      @update:model-value="listStatusFilter = $event"
                    />
                  </div>
                  <div class="po-list-filter">
                    <label class="form-label text-xs text-secondary mb-1">Project</label>
                    <base-select
                      size="sm"
                      :model-value="listProjectFilter"
                      :options="projectFilterOptions"
                      option-label="label"
                      option-value="value"
                      placeholder="All Projects"
                      @update:model-value="listProjectFilter = $event"
                    />
                  </div>
                </div>

                <div class="pm-tab-table-wrap">
                  <div class="pm-table pm-tab-table po-table">
                    <div class="pm-table-header pm-tab-table-header">
                      <span class="col-name">SUB PROJECT</span>
                      <span class="col-team text-center">TEAM</span>
                      <span class="col-date text-center">START DATE</span>
                      <span class="col-date text-center">DUE DATE</span>
                      <span class="col-pm text-center">PM / PO</span>
                      <span class="col-status text-center">STATUS</span>
                      <span v-if="listTab === 'current'" class="col-action text-center">ACTION</span>
                    </div>
                    <div class="pm-tab-table-body">
                      <div v-if="!displayItems.length" class="pm-empty-table-row">
                        <i class="fas fa-inbox fa-2x text-secondary opacity-5"></i>
                        <p class="text-secondary text-sm mt-2 mb-0">{{ emptyMessage }}</p>
                      </div>
                      <div v-for="item in displayItems" :key="item.key" class="pm-row">
                        <div class="col-name">
                          <p class="sp-name mb-0">{{ item.title }}</p>
                          <p class="sp-sub mb-0">{{ item.projectLabel }}</p>
                        </div>
                        <div class="col-team text-center">
                          <span class="pm-cell-secondary">{{ item.teamLabel }}</span>
                        </div>
                        <div class="col-date text-center">
                          <span class="pm-cell-secondary">{{ item.startLabel }}</span>
                        </div>
                        <div class="col-date text-center">
                          <span class="pm-cell-secondary">{{ item.dueLabel }}</span>
                        </div>
                        <div class="col-pm text-center">
                          <span v-if="item.ownerLabel !== '—'" class="owner-badge bo-assignee-pill">{{ item.ownerLabel }}</span>
                          <span v-else class="pm-cell-secondary">—</span>
                        </div>
                        <div class="col-status text-center">
                          <span class="status-pill" :class="item.statusPill">
                            {{ item.statusLabel }}
                          </span>
                        </div>
                        <div v-if="listTab === 'current'" class="col-action text-center">
                          <button
                            type="button"
                            class="btn btn-link btn-sm p-0 action-edit me-2"
                            title="Edit in Project Management"
                            @click="goEdit(item)"
                          >
                            <i class="fas fa-edit"></i>
                          </button>
                          <button
                            type="button"
                            class="btn btn-link btn-sm p-0 action-delete"
                            title="Delete"
                            @click="removeItem(item)"
                          >
                            <i class="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <!-- Calendar -->
              <template v-else-if="viewMode === 'calendar'">
                <div class="po-list-toolbar po-list-toolbar--end">
                  <div class="po-list-filter">
                    <label class="form-label text-xs text-secondary mb-1">Filter</label>
                    <base-select
                      size="sm"
                      :model-value="calendarLevel"
                      :options="levelOptions"
                      option-label="label"
                      option-value="value"
                      placeholder="Sub-Project"
                      @update:model-value="calendarLevel = $event"
                    />
                  </div>
                </div>

                <div v-if="allStoriesLoading && (calendarLevel === 'story' || calendarLevel === 'task')" class="text-secondary text-sm py-5 text-center flex-grow-1 d-flex align-items-center justify-content-center">
                  <span><i class="fas fa-spinner fa-spin me-2"></i>Loading {{ calendarLevel === 'task' ? 'tasks' : 'stories' }}...</span>
                </div>

                <div v-else-if="!calendarDisplayItems.length" class="text-center py-5 flex-grow-1 d-flex flex-column align-items-center justify-content-center">
                  <i class="fas fa-inbox fa-2x text-secondary opacity-5"></i>
                  <p class="text-secondary text-sm mt-2 mb-0">{{ emptyMessage }}</p>
                </div>

                <div v-else class="po-calendar">
                  <div class="po-calendar__nav mb-3">
                    <button type="button" class="po-nav-btn" @click="shiftMonth(-1)" aria-label="Previous month">
                      <i class="fas fa-chevron-left"></i>
                    </button>
                    <h6 class="mb-0 po-calendar__title">{{ calendarMonthLabel }}</h6>
                    <button type="button" class="po-nav-btn" @click="shiftMonth(1)" aria-label="Next month">
                      <i class="fas fa-chevron-right"></i>
                    </button>
                  </div>
                  <div class="po-calendar__grid">
                    <div
                      v-for="label in weekdayLabels"
                      :key="label"
                      class="po-calendar__weekday"
                    >
                      {{ label }}
                    </div>
                    <div
                      v-for="day in calendarDays"
                      :key="day.key"
                      class="po-calendar__day"
                      :class="{
                        'po-calendar__day--muted': !day.inMonth,
                        'po-calendar__day--today': day.isToday,
                      }"
                    >
                      <span class="po-calendar__num">{{ day.dayNum }}</span>
                      <div class="po-calendar__events">
                        <div
                          v-for="ev in day.events"
                          :key="ev.key"
                          class="po-calendar__event"
                          :style="{ borderLeftColor: ev.color }"
                          :title="ev.title"
                        >
                          {{ ev.shortTitle }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <!-- Gantt -->
              <template v-else>
                <div class="po-list-toolbar po-list-toolbar--end">
                  <div class="po-list-filter">
                    <label class="form-label text-xs text-secondary mb-1">Filter</label>
                    <base-select
                      size="sm"
                      :model-value="ganttLevel"
                      :options="levelOptions"
                      option-label="label"
                      option-value="value"
                      placeholder="Sub-Project"
                      @update:model-value="ganttLevel = $event"
                    />
                  </div>
                  <div class="po-list-filter">
                    <label class="form-label text-xs text-secondary mb-1">View</label>
                    <base-select
                      size="sm"
                      :model-value="ganttView"
                      :options="ganttViewOptions"
                      option-label="label"
                      option-value="value"
                      placeholder="End of Week"
                      @update:model-value="ganttView = $event"
                    />
                  </div>
                  <div class="po-list-filter">
                    <label class="form-label text-xs text-secondary mb-1">Year</label>
                    <base-select
                      size="sm"
                      :model-value="ganttYear"
                      :options="ganttYearOptions"
                      option-label="label"
                      option-value="value"
                      :placeholder="ganttYear"
                      @update:model-value="ganttYear = $event"
                    />
                  </div>
                </div>

                <div v-if="allStoriesLoading && (ganttLevel === 'story' || ganttLevel === 'task')" class="text-secondary text-sm py-5 text-center flex-grow-1 d-flex align-items-center justify-content-center">
                  <span><i class="fas fa-spinner fa-spin me-2"></i>Loading {{ ganttLevel === 'task' ? 'tasks' : 'stories' }}...</span>
                </div>

                <div v-else-if="!ganttDisplayItems.length" class="text-center py-5 flex-grow-1 d-flex flex-column align-items-center justify-content-center">
                  <i class="fas fa-inbox fa-2x text-secondary opacity-5"></i>
                  <p class="text-secondary text-sm mt-2 mb-0">{{ emptyMessage }}</p>
                </div>

                <div v-else class="po-gantt">
                  <div class="po-gantt__scroll">
                    <div class="po-gantt__header-grid" :style="[ganttTrackMinWidthStyle, ganttHeaderRowStyle]">
                      <div class="po-gantt__label-col po-gantt__label-col--merged">{{ ganttGranularityLabel }}</div>

                      <div class="po-gantt__track-col" :style="ganttTrackGridStyle">
                        <div
                          v-for="group in ganttQuarterGroups"
                          :key="`q-${group.key}`"
                          class="po-gantt__band po-gantt__band--quarter"
                          :style="{ gridColumn: `span ${group.span}` }"
                        >
                          {{ group.label }}
                        </div>
                      </div>

                      <div v-if="ganttView !== 'month'" class="po-gantt__track-col" :style="ganttTrackGridStyle">
                        <div
                          v-for="group in ganttMonthGroups"
                          :key="`m-${group.key}`"
                          class="po-gantt__band po-gantt__band--month"
                          :style="{ gridColumn: `span ${group.span}` }"
                        >
                          {{ group.label }}
                        </div>
                      </div>

                      <div class="po-gantt__track-col po-gantt__track-col--ticks" :style="ganttTrackGridStyle">
                        <div
                          v-for="tick in ganttUnitTicks"
                          :key="tick.key"
                          class="po-gantt__tick"
                          :class="{
                            'po-gantt__tick--weekend': tick.isWeekend,
                            'po-gantt__tick--holiday': tick.isHoliday,
                            'po-gantt__tick--month-start': tick.isMonthStart,
                          }"
                        >
                          {{ tick.dayLabel }}
                        </div>
                        <div
                          v-if="ganttTodayPercent !== null"
                          class="po-gantt__today-label"
                          :style="{ left: ganttTodayPercent + '%' }"
                        >
                          TODAY
                        </div>
                      </div>
                    </div>
                    <div
                      v-for="item in ganttDisplayItems"
                      :key="item.key"
                      class="po-gantt__row"
                      :style="ganttTrackMinWidthStyle"
                    >
                      <div class="po-gantt__label-col">
                        <span class="po-dot" :style="{ background: item.color }"></span>
                        <div class="po-gantt__name-wrap">
                          <span class="po-gantt__name" :title="item.title">{{ item.title }}</span>
                          <span class="po-gantt__project">{{ item.projectLabel }}</span>
                        </div>
                      </div>
                      <div class="po-gantt__track-col" :style="ganttTrackGridStyle">
                        <div
                          v-for="tick in ganttUnitTicks"
                          :key="tick.key"
                          class="po-gantt__cell"
                          :class="{
                            'po-gantt__cell--weekend': tick.isWeekend,
                            'po-gantt__cell--holiday': tick.isHoliday,
                            'po-gantt__cell--month-start': tick.isMonthStart,
                          }"
                        ></div>
                        <div
                          v-if="ganttTodayPercent !== null"
                          class="po-gantt__today-line"
                          :style="{ left: ganttTodayPercent + '%' }"
                        ></div>
                        <div
                          v-if="item.bar"
                          class="po-gantt__bar"
                          :style="{
                            left: item.bar.left,
                            width: item.bar.width,
                            background: item.color,
                          }"
                          :title="`${item.title} · ${item.rangeLabel}`"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { subProjectsApi, projectPlansApi, testPlanItemsApi } from "@/services/backendApi";
import BaseSelect from "@/components/BaseSelect.vue";
import {
  normalizeSubProjectStatus,
  subProjectStatusLabel,
  statusPillClass,
} from "@/utils/subProjectStatus";
import "@/assets/css/pm-tab-scroll.css";
import { isThaiPublicHoliday } from "@/utils/thaiHolidays";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const COLORS = ["#2152ff", "#82d616", "#fb6340", "#11cdef", "#7928ca", "#f5365c", "#2dce89", "#8392ab"];

const LEVEL_OPTIONS = [
  { value: "project", label: "Project" },
  { value: "subProject", label: "Sub-Project" },
  { value: "story", label: "Story" },
  { value: "task", label: "Task" },
];

const GANTT_VIEW_OPTIONS = [
  { value: "day", label: "Day" },
  { value: "week", label: "End of Week" },
  { value: "month", label: "Month" },
];

const ORDERING_OPTIONS = [
  { value: "due_asc", label: "Due Date" },
  { value: "launch_asc", label: "Launch Date" },
  { value: "start_asc", label: "Start Date" },
];

const TEAM_FILTER_BASE = [
  { value: "all", label: "All Teams" },
  { value: "a", label: "Team A" },
  { value: "b", label: "Team B" },
  { value: "c", label: "Team C" },
];

function parseDate(val) {
  if (!val) return null;
  const raw = String(val).slice(0, 10);
  const [y, m, d] = raw.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function dateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatDateDisplay(val) {
  if (!val) return "—";
  const s = String(val instanceof Date ? dateKey(val) : val).substring(0, 10);
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return "—";
  return `${d}-${m}-${y}`;
}

function formatRange(start, end) {
  const a = start ? formatDateDisplay(start) : null;
  const b = end ? formatDateDisplay(end) : null;
  if (a && b && a !== "—" && b !== "—") return `${a} – ${b}`;
  if (a && a !== "—") return `From ${a}`;
  if (b && b !== "—") return `Until ${b}`;
  return "No dates";
}

function teamLabel(val) {
  const v = String(val || "").trim().toLowerCase();
  return { a: "Team A", b: "Team B", all: "ALL", c: "Team C" }[v] || (v ? v.toUpperCase() : "—");
}

function compareNullableDates(a, b, desc = false) {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  const diff = a - b;
  return desc ? -diff : diff;
}

function sortItems(list, ordering) {
  const rows = [...(list || [])];
  rows.sort((a, b) => {
    switch (ordering) {
      case "launch_asc":
        return compareNullableDates(a.launchDate, b.launchDate);
      case "start_asc":
        return compareNullableDates(a.startDate, b.startDate);
      case "due_asc":
      default:
        return compareNullableDates(a.dueDate, b.dueDate);
    }
  });
  return rows;
}

function formatAssignedName(rawName) {
  const name = String(rawName || "").trim();
  if (!name) return "—";
  const nicknameMatch = name.match(/\(([^)]+)\)/);
  const firstName = name.replace(/\([^)]*\)/g, " ").trim().split(/\s+/)[0] || "";
  if (nicknameMatch && nicknameMatch[1]) {
    return `${firstName} (${nicknameMatch[1].trim()})`;
  }
  return firstName || name;
}

export default {
  name: "ProjectOverviewBoard",
  components: { BaseSelect },
  props: {
    /** "all" = every sub-project; "mine" = current user's sub-projects */
    scope: { type: String, default: "all" },
  },
  data() {
    return {
      loading: false,
      error: null,
      viewMode: "list",
      listTab: "current",
      listOrdering: "due_asc",
      listTeamFilter: "all",
      listPmFilter: "all",
      listStatusFilter: "all",
      listProjectFilter: "all",
      calendarLevel: "subProject",
      ganttLevel: "subProject",
      ganttView: "week",
      ganttYear: String(new Date().getFullYear()),
      levelOptions: LEVEL_OPTIONS,
      ganttViewOptions: GANTT_VIEW_OPTIONS,
      allStories: [],
      allStoriesLoaded: false,
      allStoriesLoading: false,
      rawSubs: [],
      calendarCursor: new Date(),
      viewOptions: [
        { key: "list", label: "List", icon: "fas fa-list" },
        { key: "calendar", label: "Calendar", icon: "fas fa-calendar-alt" },
        { key: "gantt", label: "Gantt", icon: "fas fa-stream" },
      ],
      weekdayLabels: WEEKDAY_LABELS,
      orderingOptions: ORDERING_OPTIONS,
      teamFilterOptions: TEAM_FILTER_BASE,
    };
  },
  computed: {
    ...mapState(["user"]),
    pageTitle() {
      return this.scope === "mine" ? "My Projects" : "Overview";
    },
    pageSubtitle() {
      return this.scope === "mine"
        ? "Your sub-projects and timeline — switch List, Calendar, or Gantt"
        : "All sub-projects at a glance — switch List, Calendar, or Gantt";
    },
    emptyMessage() {
      if (this.viewMode === "list" && this.hasActiveListFilters) {
        return "No sub-projects match the current filters.";
      }
      if (this.viewMode === "list" && this.listTab === "history") {
        return this.scope === "mine"
          ? "No deployed sub-projects assigned to you yet."
          : "No deployed sub-projects yet.";
      }
      return this.scope === "mine"
        ? "No sub-projects assigned to you yet."
        : "No sub-projects to show yet.";
    },
    hasActiveListFilters() {
      return (
        (this.listTeamFilter && this.listTeamFilter !== "all") ||
        (this.listPmFilter && this.listPmFilter !== "all") ||
        (this.listStatusFilter && this.listStatusFilter !== "all") ||
        (this.listProjectFilter && this.listProjectFilter !== "all")
      );
    },
    currentUserId() {
      return this.user?.userId ?? this.user?.user_id ?? null;
    },
    tabScopedItems() {
      return this.items.filter((item) =>
        this.listTab === "history" ? item.isDeployed : !item.isDeployed,
      );
    },
    pmFilterOptions() {
      const map = new Map();
      for (const item of this.tabScopedItems) {
        if (item.ownerId == null || item.ownerLabel === "—") continue;
        map.set(String(item.ownerId), item.ownerLabel);
      }
      const people = [...map.entries()]
        .sort((a, b) => a[1].localeCompare(b[1]))
        .map(([value, label]) => ({ value, label }));
      return [{ value: "all", label: "All PM / PO" }, ...people];
    },
    statusFilterOptions() {
      const map = new Map();
      for (const item of this.tabScopedItems) {
        if (!item.statusKey) continue;
        map.set(item.statusKey, item.statusLabel);
      }
      const statuses = [...map.entries()]
        .sort((a, b) => a[1].localeCompare(b[1]))
        .map(([value, label]) => ({ value, label }));
      return [{ value: "all", label: "All Status" }, ...statuses];
    },
    projectFilterOptions() {
      const map = new Map();
      for (const item of this.tabScopedItems) {
        if (item.projectId == null || !item.projectLabel || item.projectLabel === "—") continue;
        map.set(String(item.projectId), item.projectLabel);
      }
      const projects = [...map.entries()]
        .sort((a, b) => a[1].localeCompare(b[1]))
        .map(([value, label]) => ({ value, label }));
      return [{ value: "all", label: "All Projects" }, ...projects];
    },
    items() {
      const list = [];
      for (const sub of this.rawSubs || []) {
        if (this.scope === "mine") {
          const ownerId = sub.projectOwner ?? sub.project_owner;
          if (String(ownerId) !== String(this.currentUserId)) continue;
        }

        const subId = sub.subProjectId ?? sub.sub_project_id;
        const projectId = sub.projectId ?? sub.project_id ?? null;
        const title =
          String(sub.subProjectName || sub.sub_project_name || "").trim() ||
          `Sub Project ${subId || ""}`;
        const projectLabel =
          String(sub.projectName || sub.project_name || "").trim() || "—";
        const ownerRaw = String(sub.ownerFullName || sub.owner_full_name || "").trim();
        const ownerId = sub.projectOwner ?? sub.project_owner ?? null;
        const status = sub.subProjectStatus || sub.sub_project_status || "";
        const statusKey = normalizeSubProjectStatus(status);
        const launch = parseDate(sub.launchDate || sub.launch_date);
        const due = parseDate(sub.dueDate || sub.due_date);
        const startDate = parseDate(sub.planStartDate || sub.plan_start_date);
        const end = due || launch || startDate;
        const rangeStart = startDate || launch || end;

        list.push({
          key: `sp-${subId}`,
          subProjectId: subId,
          projectId: projectId != null ? String(projectId) : null,
          title,
          projectLabel,
          ownerId: ownerId != null ? String(ownerId) : null,
          ownerLabel: formatAssignedName(ownerRaw),
          teamKey: String(sub.teamTrimmed || sub.team || "").trim().toLowerCase(),
          teamLabel: teamLabel(sub.teamTrimmed || sub.team),
          statusKey,
          statusLabel: subProjectStatusLabel(status) || "—",
          statusPill: statusPillClass(status),
          isDeployed: statusKey === "deployed",
          startLabel: formatDateDisplay(startDate),
          launchLabel: formatDateDisplay(launch),
          dueLabel: formatDateDisplay(due),
          rangeLabel: formatRange(rangeStart, end),
          startDate,
          launchDate: launch,
          dueDate: due,
          start: rangeStart,
          end: end || rangeStart,
          color: COLORS[list.length % COLORS.length],
        });
      }

      return list;
    },
    displayItems() {
      let filtered = this.tabScopedItems;

      if (this.listTeamFilter && this.listTeamFilter !== "all") {
        filtered = filtered.filter((item) => item.teamKey === this.listTeamFilter);
      }
      if (this.listPmFilter && this.listPmFilter !== "all") {
        filtered = filtered.filter((item) => item.ownerId === String(this.listPmFilter));
      }
      if (this.listStatusFilter && this.listStatusFilter !== "all") {
        filtered = filtered.filter((item) => item.statusKey === this.listStatusFilter);
      }
      if (this.listProjectFilter && this.listProjectFilter !== "all") {
        filtered = filtered.filter((item) => item.projectId === String(this.listProjectFilter));
      }

      const ordered = sortItems(filtered, this.listOrdering);
      const { rangeStart, rangeEnd, spanMs } = this.ganttRange(ordered);
      return ordered.map((item) => ({
        ...item,
        bar: this.buildBar(item, rangeStart, rangeEnd, spanMs),
      }));
    },
    calendarDisplayItems() {
      return sortItems(this.buildLevelRows(this.calendarLevel), "due_asc");
    },
    ganttYearOptions() {
      const years = new Set([new Date().getFullYear()]);
      for (const item of this.items) {
        if (item.start) years.add(item.start.getFullYear());
        if (item.end) years.add(item.end.getFullYear());
      }
      return [...years].sort((a, b) => a - b).map((y) => ({ value: String(y), label: String(y) }));
    },
    ganttEffectiveRange() {
      const year = Number(this.ganttYear) || new Date().getFullYear();
      const rangeStart = new Date(year, 0, 1);
      const rangeEnd = new Date(year, 11, 31);
      // Week columns need whole weeks, so pad to Monday/Sunday — Day/Month stay exact to the chosen year.
      if (this.ganttView === "week") {
        const startDay = rangeStart.getDay();
        rangeStart.setDate(rangeStart.getDate() - (startDay === 0 ? 6 : startDay - 1));
        const endDay = rangeEnd.getDay();
        rangeEnd.setDate(rangeEnd.getDate() + (endDay === 0 ? 0 : 7 - endDay));
      }
      return { rangeStart, rangeEnd, spanMs: Math.max(1, rangeEnd - rangeStart) };
    },
    ganttDisplayItems() {
      const { rangeStart, rangeEnd, spanMs } = this.ganttEffectiveRange;
      const rows = sortItems(this.buildLevelRows(this.ganttLevel), "due_asc").filter((item) => {
        const s = item.start || item.end;
        const e = item.end || item.start;
        if (!s && !e) return false;
        return e >= rangeStart && s <= rangeEnd;
      });
      return rows.map((item) => ({
        ...item,
        bar: this.buildBar(item, rangeStart, rangeEnd, spanMs),
      }));
    },
    ganttGranularityLabel() {
      return LEVEL_OPTIONS.find((opt) => opt.value === this.ganttLevel)?.label || "Sub-Project";
    },
    calendarMonthLabel() {
      return this.calendarCursor.toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
      });
    },
    calendarDays() {
      const year = this.calendarCursor.getFullYear();
      const month = this.calendarCursor.getMonth();
      const first = new Date(year, month, 1);
      const start = new Date(first);
      start.setDate(start.getDate() - start.getDay());
      const todayKey = dateKey(new Date());
      const days = [];
      for (let i = 0; i < 42; i += 1) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        const key = dateKey(d);
        const events = this.calendarDisplayItems
          .filter((item) => {
            if (!item.start && !item.end) return false;
            const s = item.start || item.end;
            const e = item.end || item.start;
            return d >= s && d <= e;
          })
          .slice(0, 3)
          .map((item) => ({
            key: `${key}-${item.key}`,
            title: `${item.title} (${item.projectLabel})`,
            shortTitle: item.title.length > 14 ? `${item.title.slice(0, 12)}…` : item.title,
            color: item.color,
          }));
        days.push({
          key,
          dayNum: d.getDate(),
          inMonth: d.getMonth() === month,
          isToday: key === todayKey,
          events,
        });
      }
      return days;
    },
    ganttUnitTicks() {
      const { rangeStart, rangeEnd } = this.ganttEffectiveRange;
      if (!rangeStart || !rangeEnd) return [];
      const ticks = [];
      let prevMonthKey = null;
      if (this.ganttView === "month") {
        const cursor = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), 1);
        while (cursor <= rangeEnd && ticks.length < 36) {
          const month = cursor.getMonth();
          const year = cursor.getFullYear();
          const monthKey = `${year}-${month}`;
          ticks.push({
            key: monthKey,
            dayLabel: cursor.toLocaleDateString("en-GB", { month: "short" }).toUpperCase(),
            monthKey,
            monthLabel: cursor.toLocaleDateString("en-GB", { month: "long" }).toUpperCase(),
            quarterKey: `${year}-${Math.floor(month / 3)}`,
            quarterLabel: `Q${Math.floor(month / 3) + 1} ${year}`,
            isWeekend: false,
            isHoliday: false,
            isMonthStart: monthKey !== prevMonthKey,
          });
          prevMonthKey = monthKey;
          cursor.setMonth(cursor.getMonth() + 1);
        }
        return ticks;
      }

      const stepDays = this.ganttView === "day" ? 1 : 7;
      const maxTicks = this.ganttView === "day" ? 370 : 130;
      const cursor = new Date(rangeStart);
      while (cursor <= rangeEnd && ticks.length < maxTicks) {
        const labelDate = new Date(cursor);
        if (this.ganttView === "week") labelDate.setDate(labelDate.getDate() + 6);
        const month = labelDate.getMonth();
        const year = labelDate.getFullYear();
        const monthKey = `${year}-${month}`;
        const dow = cursor.getDay();
        ticks.push({
          key: dateKey(cursor),
          dayLabel: String(labelDate.getDate()),
          monthKey,
          monthLabel: labelDate.toLocaleDateString("en-GB", { month: "long" }).toUpperCase(),
          quarterKey: `${year}-${Math.floor(month / 3)}`,
          quarterLabel: `Q${Math.floor(month / 3) + 1} ${year}`,
          isWeekend: this.ganttView === "day" && (dow === 0 || dow === 6),
          isHoliday: this.ganttView === "day" && isThaiPublicHoliday(cursor),
          isMonthStart: monthKey !== prevMonthKey,
        });
        prevMonthKey = monthKey;
        cursor.setDate(cursor.getDate() + stepDays);
      }
      return ticks;
    },
    ganttMonthGroups() {
      if (this.ganttView === "month") return [];
      return this.groupConsecutiveTicks(this.ganttUnitTicks, "monthKey", "monthLabel");
    },
    ganttQuarterGroups() {
      return this.groupConsecutiveTicks(this.ganttUnitTicks, "quarterKey", "quarterLabel");
    },
    ganttTodayPercent() {
      const { rangeStart, spanMs } = this.ganttEffectiveRange;
      if (!rangeStart) return null;
      const offsetMs = new Date() - rangeStart;
      if (offsetMs < 0 || offsetMs > spanMs) return null;
      return (offsetMs / spanMs) * 100;
    },
    ganttTrackGridStyle() {
      const n = this.ganttUnitTicks.length || 1;
      return { gridAutoColumns: `minmax(2.1rem, 1fr)`, gridTemplateColumns: `repeat(${n}, minmax(2.1rem, 1fr))` };
    },
    ganttTrackMinWidthStyle() {
      const n = this.ganttUnitTicks.length || 1;
      return { minWidth: `${16 + n * 2.1}rem` };
    },
    ganttHeaderRowStyle() {
      const rows = this.ganttView === "month" ? 2 : 3;
      return { gridTemplateRows: `repeat(${rows}, auto)` };
    },
  },
  watch: {
    scope() {
      this.load();
    },
    listTab() {
      this.listPmFilter = "all";
      this.listStatusFilter = "all";
      this.listProjectFilter = "all";
    },
    calendarLevel(level) {
      if (level === "story" || level === "task") this.ensureAllStoriesLoaded();
    },
    ganttLevel(level) {
      if (level === "story" || level === "task") this.ensureAllStoriesLoaded();
    },
  },
  async created() {
    await this.load();
  },
  mounted() {
    document.documentElement.classList.add("pm-scroll-lock");
  },
  beforeUnmount() {
    document.documentElement.classList.remove("pm-scroll-lock");
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const rows = await subProjectsApi.getAll();
        this.rawSubs = Array.isArray(rows) ? rows : [];
        this.allStories = [];
        this.allStoriesLoaded = false;
      } catch (e) {
        console.error("ProjectOverviewBoard load error:", e);
        this.error = e.message || "Failed to load sub-projects";
        this.rawSubs = [];
      } finally {
        this.loading = false;
      }
    },
    async removeItem(item) {
      if (!item?.subProjectId) return;
      if (!confirm(`Delete "${item.title}"?`)) return;
      try {
        await subProjectsApi.delete(item.subProjectId);
        await this.load();
      } catch (e) {
        console.error("removeItem error:", e);
        alert(e.message || "Failed to delete sub-project");
      }
    },
    goEdit(item) {
      this.$router.push({ name: "Tables", query: { tab: "subproject", edit: item.subProjectId } });
    },
    buildLevelRows(level) {
      let rows;
      if (level === "project") {
        rows = this.buildProjectRows();
      } else if (level === "story") {
        rows = this.allStories.map((s, idx) => {
          const start = parseDate(s.planOverallStart);
          const end = parseDate(s.planOverallDue) || start;
          return {
            key: `story-${s.testPlanId}`,
            title: s.testPlanName || `Story ${s.testPlanId}`,
            projectLabel: s.subProjectTitle || "—",
            color: COLORS[idx % COLORS.length],
            startDate: start,
            dueDate: end,
            launchDate: null,
            start: start || end,
            end: end || start,
            rangeLabel: formatRange(start, end),
          };
        });
      } else if (level === "task") {
        rows = [];
        let idx = 0;
        for (const s of this.allStories) {
          for (const t of s.subTasks || []) {
            const start = parseDate(t.startDate);
            const end = parseDate(t.dueDate) || start;
            rows.push({
              key: `task-${s.testPlanId}-${idx}`,
              title: t.name || `Task ${idx + 1}`,
              projectLabel: s.subProjectTitle || "—",
              color: COLORS[idx % COLORS.length],
              startDate: start,
              dueDate: end,
              launchDate: null,
              start: start || end,
              end: end || start,
              rangeLabel: formatRange(start, end),
            });
            idx += 1;
          }
        }
      } else {
        rows = this.items.filter((item) => !item.isDeployed);
      }

      return rows;
    },
    buildProjectRows() {
      const map = new Map();
      for (const item of this.items) {
        if (item.isDeployed || item.projectId == null) continue;
        if (!map.has(item.projectId)) {
          map.set(item.projectId, {
            key: `project-${item.projectId}`,
            title: item.projectLabel,
            starts: [],
            ends: [],
          });
        }
        const g = map.get(item.projectId);
        if (item.start) g.starts.push(item.start);
        if (item.end) g.ends.push(item.end);
      }
      return [...map.values()].map((g, idx) => {
        const start = g.starts.length ? new Date(Math.min(...g.starts.map((d) => d.getTime()))) : null;
        const end = g.ends.length ? new Date(Math.max(...g.ends.map((d) => d.getTime()))) : null;
        return {
          key: g.key,
          title: g.title,
          projectLabel: "",
          color: COLORS[idx % COLORS.length],
          startDate: start,
          dueDate: end,
          launchDate: null,
          start: start || end,
          end: end || start,
          rangeLabel: formatRange(start, end),
        };
      });
    },
    async ensureAllStoriesLoaded() {
      if (this.allStoriesLoaded || this.allStoriesLoading) return;
      this.allStoriesLoading = true;
      try {
        const subs = this.items.filter((item) => !item.isDeployed);
        const results = await Promise.all(
          subs.map(async (sub) => {
            try {
              const plan = await projectPlansApi.getIdBySubProject(sub.subProjectId);
              if (!plan?.projectPlanId) return [];
              const stories = await testPlanItemsApi.getByProjectPlan(plan.projectPlanId);
              return (Array.isArray(stories) ? stories : []).map((s) => ({
                ...s,
                subProjectTitle: sub.title,
              }));
            } catch (e) {
              console.error("ensureAllStoriesLoaded story fetch error:", e);
              return [];
            }
          }),
        );
        this.allStories = results.flat();
        this.allStoriesLoaded = true;
      } finally {
        this.allStoriesLoading = false;
      }
    },
    shiftMonth(delta) {
      const d = new Date(this.calendarCursor);
      d.setMonth(d.getMonth() + delta);
      this.calendarCursor = d;
    },
    ganttRange(items) {
      const dated = (items || []).filter((i) => i.start || i.end);
      const now = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
      let rangeStart;
      let rangeEnd;
      if (!dated.length) {
        rangeStart = new Date(now);
        rangeEnd = new Date(now);
        rangeEnd.setDate(rangeEnd.getDate() + 7 * 8);
      } else {
        const starts = dated.map((i) => (i.start || i.end).getTime());
        const ends = dated.map((i) => (i.end || i.start).getTime());
        rangeStart = new Date(Math.min(...starts, now.getTime()));
        rangeEnd = new Date(Math.max(...ends, now.getTime()));
      }
      // Snap to week boundaries (Monday start) so the week/month/quarter header aligns with bars.
      const startDay = rangeStart.getDay();
      rangeStart.setDate(rangeStart.getDate() - (startDay === 0 ? 6 : startDay - 1));
      const endDay = rangeEnd.getDay();
      rangeEnd.setDate(rangeEnd.getDate() + (endDay === 0 ? 0 : 7 - endDay));
      return {
        rangeStart,
        rangeEnd,
        spanMs: Math.max(1, rangeEnd - rangeStart),
      };
    },
    groupConsecutiveTicks(ticks, keyField, labelField) {
      const groups = [];
      for (const t of ticks) {
        const last = groups[groups.length - 1];
        if (last && last.key === t[keyField]) {
          last.span += 1;
        } else {
          groups.push({ key: t[keyField], label: t[labelField], span: 1 });
        }
      }
      return groups;
    },
    buildBar(item, rangeStart, rangeEnd, spanMs) {
      if (!item.start && !item.end) return null;
      const s = item.start || item.end;
      const e = item.end || item.start;
      const leftMs = Math.max(0, s - rangeStart);
      const rightMs = Math.min(spanMs, e - rangeStart);
      const widthMs = Math.max(rightMs - leftMs, spanMs * 0.02);
      return {
        left: `${(leftMs / spanMs) * 100}%`,
        width: `${(widthMs / spanMs) * 100}%`,
      };
    },
  },
};
</script>

<style scoped>
.pm-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
}

.pm-page-main {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.pm-page-col {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.pm-page-card {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.pm-page-tab-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.card-header.pb-0 {
  padding-top: 1rem !important;
  padding-bottom: 1rem !important;
  background: transparent;
  border-bottom: none;
}

.tab-shell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.3rem;
  border-radius: 12px;
  background: transparent;
}

.tab-item {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #696969;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  padding: 0.55rem 0.9rem;
  line-height: 1.2;
  border: none;
  border-radius: 12px;
  background: #f5f5f5;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-item.active {
  color: #ffffff;
  background: #344767;
  box-shadow: 0 6px 14px rgba(52, 71, 103, 0.28);
}

.view-toggle {
  display: inline-flex;
  gap: 0.25rem;
  padding: 0.25rem;
  border-radius: 10px;
  background: #eef1f6;
}

.view-toggle__btn {
  border: none;
  background: transparent;
  border-radius: 8px;
  padding: 0.4rem 0.75rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #67748e;
  cursor: pointer;
}

.view-toggle__btn--active {
  background: #fff;
  color: #344767;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.po-dot {
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-block;
  box-shadow: 0 0 0 3px rgba(52, 71, 103, 0.08);
}

/* Same role as Plan Management .plan-mgmt-inset */
.po-mgmt-inset {
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  padding: 1rem;
}

.po-list-toolbar {
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.85rem;
  position: relative;
  z-index: 20;
  overflow: visible;
}

.po-list-filter {
  min-width: 150px;
  width: 165px;
  position: relative;
  z-index: 21;
  overflow: visible;
}

.po-list-toolbar--end {
  justify-content: flex-end;
}

.po-mgmt-inset > .pm-tab-table-wrap {
  min-width: 0;
  width: 100%;
}

.po-mgmt-inset > .pm-tab-table-wrap > .pm-table.pm-tab-table {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.po-mgmt-inset .pm-table-header {
  display: flex;
  align-items: center;
  padding: 0.65rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.7rem;
  font-weight: 700;
  color: #8392ab;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.po-mgmt-inset .pm-row {
  display: flex;
  align-items: center;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #f1f3f5;
  transition: background 0.15s;
}

.po-mgmt-inset .pm-row:last-child {
  border-bottom: none;
}

.po-mgmt-inset .pm-row:hover {
  background: #fafbfc;
}

.col-name {
  flex: 2.4;
  min-width: 0;
}

.col-date {
  flex: 1;
  min-width: 78px;
}

.col-team {
  flex: 0.75;
  min-width: 58px;
}

.col-pm {
  flex: 0.85;
  min-width: 72px;
}

.col-status {
  flex: 1.05;
  min-width: 82px;
}

.col-action {
  flex: 0.65;
  min-width: 52px;
}

.sp-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: #4361ee;
  text-decoration: underline dotted;
  line-height: 1.35;
}

.sp-sub {
  font-size: 0.78rem;
  color: #8392ab;
  margin-top: 2px;
}

.pm-cell-secondary {
  font-size: 0.8rem;
  color: #67748e;
}

.owner-badge {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: #e8f4fd;
  color: #0c7a9a;
  border: 1px solid #bee5eb;
  border-radius: 50px;
  font-size: 0.65rem;
  font-weight: 500;
  padding: 0.02rem 0.2rem;
  line-height: 1.1;
}

.bo-assignee-pill {
  background: #e8f4fd !important;
  color: #0c7a9a !important;
  border-color: #bee5eb !important;
}

.col-action .btn-link {
  text-decoration: none;
  line-height: 1;
}

.col-action .btn-link i {
  font-size: 0.85rem;
}

.action-edit { color: #4361ee; }
.action-edit:hover { color: #2d4bc8; }
.action-delete { color: #e74c3c; }
.action-delete:hover { color: #c0392b; }

.po-calendar {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.po-gantt {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.po-calendar__nav {
  display: grid;
  grid-template-columns: 2rem 1fr 2rem;
  align-items: center;
  gap: 0.5rem;
}

.po-calendar__title {
  text-align: center;
  font-weight: 700;
  color: #344767;
}

.po-nav-btn {
  width: 2rem;
  height: 2rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #fff;
  color: #67748e;
  cursor: pointer;
}

.po-calendar__grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
}

.po-calendar__weekday {
  padding: 0.5rem;
  font-size: 0.65rem;
  font-weight: 700;
  text-align: center;
  color: #8392ab;
  background: #fafbfc;
  border-bottom: 1px solid #e9ecef;
}

.po-calendar__day {
  min-height: 5.5rem;
  padding: 0.35rem;
  border-right: 1px solid #e9ecef;
  border-bottom: 1px solid #e9ecef;
  background: #fff;
}

.po-calendar__day:nth-child(7n) {
  border-right: none;
}

.po-calendar__day--muted {
  background: #f8f9fa;
  opacity: 0.65;
}

.po-calendar__day--today .po-calendar__num {
  background: #2152ff;
  color: #fff;
  border-radius: 999px;
  width: 1.35rem;
  height: 1.35rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.po-calendar__num {
  font-size: 0.75rem;
  font-weight: 700;
  color: #344767;
}

.po-calendar__events {
  margin-top: 0.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.po-calendar__event {
  font-size: 0.58rem;
  font-weight: 600;
  color: #344767;
  background: #f4f6f9;
  border-left: 3px solid #2152ff;
  border-radius: 3px;
  padding: 0.1rem 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.po-gantt__scroll {
  overflow-x: auto;
}

.po-gantt__header-grid {
  display: grid;
  grid-template-columns: 16rem 1fr;
  gap: 0;
  align-items: stretch;
  border-bottom: 2px solid #ced4da;
}

.po-gantt__row {
  display: grid;
  grid-template-columns: 16rem 1fr;
  gap: 0;
  align-items: stretch;
  border-bottom: 1px solid #dee2e6;
}

.po-gantt__label-col {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  font-size: 0.8rem;
  font-weight: 600;
  color: #344767;
  padding: 0.4rem 0.75rem;
  position: sticky;
  left: 0;
  z-index: 4;
  background: #fff;
  border-right: 2px solid #ced4da;
}

.po-gantt__label-col--merged {
  grid-row: 1 / -1;
  grid-column: 1;
  justify-content: center;
  text-align: center;
  border: 1px solid #ced4da;
}

.po-gantt__band {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  padding: 0.3rem 0.4rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.po-gantt__band--quarter {
  background: #344767;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
}

.po-gantt__band--month {
  background: #67748e;
  border-right: 1px solid rgba(255, 255, 255, 0.25);
  font-size: 0.62rem;
}

.po-gantt__name-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

.po-gantt__name {
  white-space: normal;
  word-break: break-word;
  line-height: 1.3;
  font-size: 0.82rem;
}

.po-gantt__project {
  font-size: 0.68rem;
  font-weight: 500;
  color: #8392ab;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.po-gantt__track-col {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: 0;
  position: relative;
}

.po-gantt__tick,
.po-gantt__cell {
  border-right: 1px solid #dee2e6;
}

.po-gantt__tick {
  font-size: 0.62rem;
  font-weight: 700;
  color: #8392ab;
  text-align: center;
  padding: 0.3rem 0;
  background: #fafbfc;
}

.po-gantt__cell {
  background: #fff;
  min-height: 2.2rem;
}

.po-gantt__tick--weekend,
.po-gantt__cell--weekend {
  background: #e9ecef;
}

.po-gantt__tick--holiday,
.po-gantt__cell--holiday {
  background: #dfe3e8;
}

.po-gantt__tick--month-start,
.po-gantt__cell--month-start {
  border-left: 2px solid #8392ab;
}

.po-gantt__today-label {
  position: absolute;
  top: 0.1rem;
  transform: translateX(-50%);
  background: #2152ff;
  color: #fff;
  font-size: 0.58rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 0.1rem 0.35rem;
  border-radius: 999px;
  white-space: nowrap;
  z-index: 3;
}

.po-gantt__today-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #2152ff;
  opacity: 0.55;
  z-index: 1;
}

.po-gantt__bar {
  position: absolute;
  top: 50%;
  height: 1.1rem;
  margin-top: -0.55rem;
  border-radius: 4px;
  opacity: 0.9;
  min-width: 0.5rem;
  z-index: 2;
}

</style>
